import { useState, useRef, useCallback, useEffect } from 'react';
import { formatDate, todayInputValue } from '../../../../utils/format';
import { showToast } from '../../../../components/base/Toast';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import { useCategories } from '../../../../hooks/useCategories';
import { MATERIAL_GROUP_FALLBACK, UNIT_OF_MEASURE_FALLBACK } from '../../../../constants/categoryFallbacks';
import { uploadFile } from '../../../../api/file';
import type { DiaryAttachment, Material, Season } from '../../../../types';

// Real backend MaterialStatus values (PENDING_ISSUE/ISSUED/IN_USE/RETURNED/
// DEPLETED) — 'Đủ'/'Sắp hết'/'Thiếu' belong to SupplyItem, not Material, and
// have no backend equivalent here.
const STATUS_OPTIONS: Material['status'][] = ['Chờ cấp', 'Đã cấp', 'Đang sử dụng', 'Đã trả', 'Đã hết'];

interface MaterialFormModalProps {
  editItem?: Material | null;
  seasons: Season[];
  onSave: (data: {
    tenVatTu: string;
    nhomVatTu: string;
    nguonGoc: Material['nguonGoc'];
    soLuong: number;
    tonKho: number;
    donVi: string;
    ngayCap: string;
    seasonId: string;
    ghiChu?: string;
    status: Material['status'];
    attachments?: DiaryAttachment[];
  }) => void;
  onClose: () => void;
}

export function MaterialFormModal({ editItem, seasons, onSave, onClose }: MaterialFormModalProps) {
  useModalBackHandler(true, onClose);
  const isEdit = !!editItem;
  const { names: materialGroups } = useCategories('MATERIAL_GROUP', MATERIAL_GROUP_FALLBACK);
  const { names: unitOptions } = useCategories('UNIT_OF_MEASURE', UNIT_OF_MEASURE_FALLBACK);

  const [tenVatTu, setTenVatTu] = useState(editItem?.tenVatTu || '');
  const [nhomVatTu, setNhomVatTu] = useState(editItem?.nhomVatTu || '');
  const [nguonGoc, setNguonGoc] = useState<Material['nguonGoc']>(editItem?.nguonGoc || 'Nông hộ tự mua');
  // "Số lượng tồn" is the only quantity input shown to the user — it drives both
  // soLuong and tonKho on submit (see handleSubmit), since there is no separate
  // control for tonKho and leaving it unset was silently saving as 0.
  const [soLuong, setSoLuong] = useState(String(editItem?.tonKho ?? editItem?.soLuong ?? ''));
  const [donVi, setDonVi] = useState(editItem?.donVi || 'kg');
  const [ngayCap, setNgayCap] = useState(editItem?.ngayCap || todayInputValue());
  const [seasonId, setSeasonId] = useState(editItem?.seasonId || seasons[0]?.id || '');
  const [ghiChu, setGhiChu] = useState(editItem?.ghiChu || '');
  const [status, setStatus] = useState<Material['status']>(editItem?.status || 'Đủ');

  const [attachments, setAttachments] = useState<DiaryAttachment[]>([]);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!nhomVatTu && materialGroups.length > 0) setNhomVatTu(materialGroups[0]);
  }, [materialGroups, nhomVatTu]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (fileRef.current) fileRef.current.value = '';
    if (!files || files.length === 0) return;
    setUploadingCount((n) => n + files.length);
    for (const file of Array.from(files)) {
      const localUrl = URL.createObjectURL(file);
      try {
        const fileId = await uploadFile(file, 'MATERIAL_ATTACHMENT', file.type);
        const type: DiaryAttachment['type'] = file.type.startsWith('video') ? 'video' : 'image';
        setAttachments((prev) => [...prev, { fileId, type, caption: file.name }]);
        setPreviewUrls((prev) => ({ ...prev, [fileId]: localUrl }));
      } catch {
        showToast(`Tải lên "${file.name}" thất bại`, 'error');
      } finally {
        setUploadingCount((n) => n - 1);
      }
    }
  }, []);

  const removeAttachment = (fileId: string) => setAttachments((prev) => prev.filter((a) => a.fileId !== fileId));

  const handleSubmit = () => {
    // Editing an existing record only supports a status change on the real
    // backend (PATCH /materials/:id/status) — skip the create-only field
    // validations below, which don't apply since nothing else is editable.
    if (isEdit) {
      onSave({
        tenVatTu, nhomVatTu, nguonGoc,
        soLuong: Number(soLuong) || 0, tonKho: Number(soLuong) || 0,
        donVi, ngayCap, seasonId, ghiChu: ghiChu.trim() || undefined, status,
      });
      return;
    }

    const soLuongNum = Number(soLuong);
    if (!tenVatTu.trim()) {
      showToast('Vui lòng nhập tên vật tư', 'warning');
      return;
    }
    if (!soLuongNum || soLuongNum <= 0) {
      showToast('Vui lòng nhập số lượng hợp lệ', 'warning');
      return;
    }
    if (!donVi.trim()) {
      showToast('Vui lòng nhập đơn vị', 'warning');
      return;
    }
    if (!ngayCap) {
      showToast('Vui lòng chọn ngày nhập', 'warning');
      return;
    }
    if (uploadingCount > 0) {
      showToast('Đang tải lên ảnh, vui lòng đợi', 'warning');
      return;
    }

    onSave({
      tenVatTu: tenVatTu.trim(),
      nhomVatTu,
      nguonGoc,
      soLuong: soLuongNum,
      tonKho: soLuongNum,
      donVi: donVi.trim(),
      ngayCap,
      seasonId,
      ghiChu: ghiChu.trim() || undefined,
      status,
      attachments: attachments.length > 0 ? attachments : undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={onClose} />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-slide-up">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
          <h3 className="text-base font-semibold text-slate-900">
            {isEdit ? 'Sửa vật tư' : 'Thêm vật tư mới'}
          </h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Tên vật tư <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={tenVatTu}
              onChange={(e) => setTenVatTu(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              placeholder="Tên vật tư"
              disabled={isEdit}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nhóm vật tư</label>
            <select
              value={nhomVatTu}
              onChange={(e) => setNhomVatTu(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              disabled={isEdit}
            >
              {materialGroups.map((n) => (<option key={n} value={n}>{n}</option>))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nguồn vật tư</label>
            <select
              value={nguonGoc}
              onChange={(e) => setNguonGoc(e.target.value as Material['nguonGoc'])}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              disabled={isEdit}
            >
              <option value="Nông hộ tự mua">Nông hộ tự mua</option>
              <option value="HTX cấp/ứng">HTX cấp/ứng</option>
            </select>
            {isEdit && (
              <p className="text-xs text-sky-600 mt-1">Chỉ có thể đổi trạng thái vật tư đã tạo — các thông tin khác không thể sửa.</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Số lượng tồn <span className="text-red-500">*</span></label>
              <input
                type="number"
                value={soLuong}
                onChange={(e) => setSoLuong(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                placeholder="Số lượng"
                disabled={isEdit}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Đơn vị <span className="text-red-500">*</span></label>
              <input
                type="text"
                list="unit-of-measure-options"
                value={donVi}
                onChange={(e) => setDonVi(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                placeholder="kg, lít, cái..."
              />
              <datalist id="unit-of-measure-options">
                {unitOptions.map((u) => (<option key={u} value={u} />))}
              </datalist>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ngày nhập</label>
              <input
                type="date"
                value={ngayCap}
                onChange={(e) => setNgayCap(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
                disabled={isEdit}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Trạng thái</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as Material['status'])}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              >
                {STATUS_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Mùa vụ liên quan</label>
            <select
              value={seasonId}
              onChange={(e) => setSeasonId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              disabled={isEdit}
            >
              <option value="">Chọn mùa vụ</option>
              {seasons.map((s) => (
                <option key={s.id} value={s.id}>{s.maMuaVu} - {s.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú</label>
            <input
              type="text"
              value={ghiChu}
              onChange={(e) => setGhiChu(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500"
              placeholder="Ghi chú..."
            />
          </div>

          {!isEdit && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Ảnh / chứng từ nếu có</label>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="px-3 py-2 rounded-xl border border-slate-200 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center gap-1 whitespace-nowrap"
              >
                <span className="w-3 h-3 flex items-center justify-center"><i className="ri-image-line" /></span>
                Chọn ảnh
              </button>
              <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleFileSelect} />
              {uploadingCount > 0 && <span className="ml-2 text-xs text-slate-400 align-middle">Đang tải {uploadingCount}...</span>}
              {attachments.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                  {attachments.map((a) => (
                    <div key={a.fileId} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100">
                      {a.type === 'video' ? (
                        <video src={previewUrls[a.fileId]} className="w-full h-full object-cover" />
                      ) : (
                        <img src={previewUrls[a.fileId]} alt={a.caption ?? ''} className="w-full h-full object-cover" />
                      )}
                      <button
                        type="button"
                        onClick={() => removeAttachment(a.fileId)}
                        className="absolute top-1 right-1 w-5 h-5 bg-black/50 rounded-full text-white flex items-center justify-center text-xs"
                      >
                        <i className="ri-close-line" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-md text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]"
          >
            Hủy
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2.5 rounded-md text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 transition-colors whitespace-nowrap min-h-[44px]"
          >
            {isEdit ? 'Lưu thay đổi' : 'Thêm mới'}
          </button>
        </div>
      </div>
    </div>
  );
}